import { Handle, Position } from '@xyflow/react'
import { CheckCircle2, Cog, MessageSquare, Wrench } from 'lucide-react'
import type { ApprovalStepConfig, InfoGateStepConfig, WorkflowStepConfig } from '@kfolks/types'
import { isAdvancedStep } from './advanced-step'

/**
 * Single dispatcher node renderer for the visual workflow builder.
 *
 * `@xyflow/react` registers one "node type" — `'workflow-step'` — and
 * `WorkflowStepNode` switches on the step's `type` discriminator
 * internally to render the right per-type body. This is the natural
 * shape for a discriminated union: narrowing at the dispatch site
 * gives end-to-end type safety with no casts at the canvas-typing
 * seam (which previously needed `as unknown as NodeTypes` because
 * xyflow's `ComponentType<NodeProps>` and our narrower per-step
 * renderers were variance-incompatible).
 *
 * The `default: never` branch in the switch is the compile-time
 * exhaustiveness check — adding a new `WorkflowStepConfig` discriminator
 * value without a matching case is a type error.
 *
 * Each per-type body renders the step's identity (id + label + icon)
 * plus a small type-specific summary (approver count, info-gate field
 * count). `Handle` components define source / target attachment points
 * for read-only edges (rendered from `after.next` references in the
 * canvas).
 */
export function WorkflowStepNode({ data }: { data: { step: WorkflowStepConfig; isStart?: boolean } }) {
	const step = data.step
	const advanced = isAdvancedStep(step)
	let body: React.ReactNode
	switch (step.type) {
		case 'approval':
			body = <ApprovalNodeBody step={step} />
			break
		case 'info_gate':
			body = <InfoGateNodeBody step={step} />
			break
		default: {
			const _exhaustive: never = step
			void _exhaustive
			return null
		}
	}
	const wrapped = advanced ? <AdvancedNodeWrapper step={step}>{body}</AdvancedNodeWrapper> : body
	if (!data.isStart) return <>{wrapped}</>
	return (
		<div className="relative">
			{wrapped}
			<div
				className="absolute -top-2 left-2 rounded-full border border-emerald-500/70 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 shadow-sm dark:bg-emerald-950/40 dark:text-emerald-400"
				data-testid="workflow-start-badge"
				title="The workflow begins here when a request is submitted."
			>
				Start
			</div>
		</div>
	)
}

/**
 * Wraps a step node body with an "Advanced" badge when the step has
 * fields the visual builder doesn't surface (e.g., a non-trivial
 * `after.guard`). Per the workflow-builder capability spec the body
 * still renders, but the admin's authoring entry point is the Raw JSON
 * tab. The page-level banner (in `VisualTab`) explains the round-trip
 * semantics; the per-node badge anchors which step(s) trigger it.
 */
function AdvancedNodeWrapper({ step, children }: { step: WorkflowStepConfig; children: React.ReactNode }) {
	return (
		<div className="relative" data-step-id={step.id} data-advanced="true">
			{children}
			<div
				className="absolute -top-2 right-2 flex items-center gap-1 rounded-md border border-amber-500/60 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 shadow-sm dark:bg-amber-950/40 dark:text-amber-400"
				data-testid={`advanced-badge-${step.id}`}
				title="This step has fields the visual builder doesn't surface — edit in Raw JSON."
			>
				<Wrench className="size-3" />
				Advanced
			</div>
		</div>
	)
}

// ─── Per-type bodies ─────────────────────────────────────────────────────────

const NODE_BASE_CLASS =
	'w-60 rounded-md border bg-background px-3 py-2 text-sm shadow-sm transition-colors hover:bg-accent/30'
const NODE_ICON_CLASS = 'mt-0.5 size-4 shrink-0 text-muted-foreground'

function ApprovalNodeBody({ step }: { step: ApprovalStepConfig }) {
	const kind = step.kind ?? 'decision'
	// Decision approvals (manager review, finance sign-off) use the check-circle.
	// Action approvals (IT provisioning, ops actuation) use the cog — same engine
	// path, just a UX cue that the actor is performing work rather than deciding.
	const Icon = kind === 'action' ? Cog : CheckCircle2
	const subtypeLabel = kind === 'action' ? 'approval · action' : 'approval · decision'
	return (
		<div className={NODE_BASE_CLASS} data-step-id={step.id} data-step-type="approval" data-step-kind={kind}>
			<Handle type="target" position={Position.Top} />
			<div className="flex items-start gap-2">
				<Icon className={NODE_ICON_CLASS} />
				<div className="flex-1 min-w-0">
					<p className="truncate font-medium">{step.label || step.id}</p>
					<p className="font-mono text-xs text-muted-foreground">
						{step.id} · {subtypeLabel}
					</p>
					{step.slaHours ? <p className="mt-1 text-xs text-muted-foreground">{step.slaHours}h SLA</p> : null}
				</div>
			</div>
			<Handle type="source" position={Position.Bottom} />
		</div>
	)
}

function InfoGateNodeBody({ step }: { step: InfoGateStepConfig }) {
	const fieldCount = step.fields?.length ?? 0
	return (
		<div className={NODE_BASE_CLASS} data-step-id={step.id} data-step-type="info_gate">
			<Handle type="target" position={Position.Top} />
			<div className="flex items-start gap-2">
				<MessageSquare className={NODE_ICON_CLASS} />
				<div className="flex-1 min-w-0">
					<p className="truncate font-medium">{step.label || step.id}</p>
					<p className="font-mono text-xs text-muted-foreground">{step.id} · info_gate</p>
					<p className="mt-1 text-xs text-muted-foreground">
						{fieldCount === 0 ? 'No fields configured' : `${fieldCount} field${fieldCount === 1 ? '' : 's'}`}
						{step.slaHours ? ` · ${step.slaHours}h SLA` : ''}
					</p>
				</div>
			</div>
			<Handle type="source" position={Position.Bottom} />
		</div>
	)
}
